import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import type {
  UseFormGetValues,
  UseFormSetFocus,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import type { NavigateFunction } from 'react-router-dom';

import { useMainPatientSearch } from '@hooks/main';
import type { Clinic } from '@app-types/auth';
import type { ToastMethods } from '@/hooks/useAppToast';
import type useAppModal from '@/hooks/useAppModal';
import {
  useSearchMedicalAidInsurers,
  type MedicalAidInsurer,
} from '@/hooks/main/useMedicalAidSearch';
import { useInsurerDialogMessages } from '@/hooks/main/useInsurerDialogMessages';
import {
  PATIENT_ADDRESS_KANA_MAX_LENGTH,
  PatientFormData,
  PatientFormSchemaType,
} from '@pages/main/patient/types';
import { fetchInsurerDetail } from '@pages/main/patient/services';
import { normalizeInsurerNumber as normalizeInsurerNumberField } from '@/utils/japaneseText';
import { isMedicalAidNoClaim } from '@pages/main/patient/utils/medicalAidCalc';
import { findExactSerialNumberMatch } from '@pages/main/patient/utils/patientSerialNumberMatch';
import { toOptionalNumber } from './usePatientEditPage.formValues';
import { useInsurerSearch, type InsurerSearchResult } from './useInsurerSearch';

type AppModal = ReturnType<typeof useAppModal>;

interface UsePatientEditPageSearchHandlersArgs {
  searchKeyword: string;
  includeExcluded: boolean;
  setSearchKeyword: (value: string) => void;
  tenant?: string;
  navigate: NavigateFunction;
  modal: AppModal;
  toast: ToastMethods;
  clinic: Clinic | null;
  watch: UseFormWatch<PatientFormSchemaType>;
  setValue: UseFormSetValue<PatientFormSchemaType>;
  getValues: UseFormGetValues<PatientFormSchemaType>;
  setFocus: UseFormSetFocus<PatientFormSchemaType>;
  originalMedicalAidInsurerTypeIdRef: MutableRefObject<number | null | undefined>;
  onInsurerSelectedNonKoreiRef: MutableRefObject<(() => void) | undefined>;
}

export const usePatientEditPageSearchHandlers = ({
  searchKeyword,
  includeExcluded,
  setSearchKeyword,
  tenant,
  navigate,
  modal,
  toast,
  clinic,
  watch,
  setValue,
  getValues,
  setFocus,
  originalMedicalAidInsurerTypeIdRef,
  onInsurerSelectedNonKoreiRef,
}: UsePatientEditPageSearchHandlersArgs) => {
  const searchMutation = useMainPatientSearch();
  const {
    getWarningDialog1Message,
    getMedicalAidWarningDialog2Message,
    getMedicalAidNoClaimMessage,
  } = useInsurerDialogMessages();

  const [isPatientSearchModalOpen, setIsPatientSearchModalOpen] = useState(false);
  const [isAddressSearchModalOpen, setIsAddressSearchModalOpen] = useState(false);
  const [isInsuranceAddressSearchModalOpen, setIsInsuranceAddressSearchModalOpen] =
    useState(false);
  const [isInsurerSearchModalOpen, setIsInsurerSearchModalOpen] = useState(false);
  const [pendingInsurerResults, setPendingInsurerResults] = useState<InsurerSearchResult[] | null>(
    null
  );
  const [pendingInsurerTotalCount, setPendingInsurerTotalCount] = useState(0);
  const [isKoreiModalOpen, setIsKoreiModalOpen] = useState(false);
  const [pendingKoreiInsurer, setPendingKoreiInsurer] = useState<any>(null);
  const [isMedicalAidSearchModalOpen, setIsMedicalAidSearchModalOpen] = useState(false);
  const [pendingMedicalAidIntervalRows, setPendingMedicalAidIntervalRows] = useState<
    MedicalAidInsurer[]
  >([]);
  const [isMedicalAidDirectSearchLoading, setIsMedicalAidDirectSearchLoading] = useState(false);

  const handlePatientSearch = useCallback(() => {
    const keyword = (searchKeyword || '').trim();

    searchMutation.mutate(
      {
        keyword,
        includeExcluded,
      },
      {
        onSuccess: result => {
          const exactSerialNumberMatch = findExactSerialNumberMatch(result.data, keyword);

          const exactSerialNumberMatchId =
            exactSerialNumberMatch?.patient?.patient_id ?? exactSerialNumberMatch?.id;

          if (exactSerialNumberMatchId) {
            // HIT-14783: the patient is loaded into the form, so the search box is cleared.
            setSearchKeyword('');
            const navUrl = `/${tenant}/patients/edit/${exactSerialNumberMatchId}`;
            navigate(navUrl);
          } else if (result.data.length === 0) {
            modal.confirm({
              title: '確認',
              content: '対象受診者が存在しませんでした。新規登録しますか？',
              okText: 'はい',
              cancelText: 'いいえ',
              onOk: () => {
                setSearchKeyword('');
                const navUrl = `/${tenant}/patients/edit`;
                navigate(navUrl);
              },
              onCancel: () => {},
            });
          } else {
            // Always show modal for multiple results
            setIsPatientSearchModalOpen(true);
          }
        },
        onError: error => {
          console.error('[PatientSearch] Search failed', error);
        },
      }
    );
  }, [searchKeyword, includeExcluded, searchMutation, navigate, tenant, modal, setSearchKeyword]);

  const handlePatientSearchClose = useCallback(() => {
    setIsPatientSearchModalOpen(false);
  }, []);

  const handlePatientSearchSelect = useCallback(
    (selectedPatient: PatientFormData) => {
      setIsPatientSearchModalOpen(false);
      toast.success('患者情報を読み込みました');

      // Navigate to the patient edit page with the selected patient's ID
      // The full patient data will be fetched via useMainPatientEdit hook after navigation
      if (selectedPatient.patient?.patient_id && tenant) {
        navigate(`/${tenant}/patients/edit/${selectedPatient.patient?.patient_id}`);
      }
    },
    [navigate, tenant, toast]
  );

  const handleAddressSearch = useCallback(() => {
    setIsAddressSearchModalOpen(true);
  }, []);

  const handleAddressSearchClose = useCallback(() => {
    setIsAddressSearchModalOpen(false);
  }, []);

  const handleAddressSearchSelect = useCallback(
    (result: {
      postal_code1: string;
      postal_code2: string;
      address: string;
      address_kana: string;
    }) => {
      setValue('patient_information.postal_code1', result.postal_code1, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue('patient_information.postal_code2', result.postal_code2, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue('patient_information.address1', result.address, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(
        'patient_information.address_kana',
        (result.address_kana || '').slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH),
        {
          shouldDirty: true,
          shouldTouch: true,
        }
      );
      setIsAddressSearchModalOpen(false);
      // toast.success('住所情報を設定しました');
    },
    [setValue]
  );

  const handleOpenMap = useCallback(() => {
    const address1 = watch('patient_information.address1');
    // Match A2 Blade behavior: do nothing if address1 is empty
    if (!address1 || address1.trim() === '') {
      return;
    }
    const address =
      address1 +
      watch('patient_information.address2') +
      watch('patient_information.address3') +
      watch('patient_information.address_name');
    const mapsUrl = `https://www.google.co.jp/maps?q=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  }, [watch]);

  const handleInsuranceAddressSearch = useCallback(() => {
    setIsInsuranceAddressSearchModalOpen(true);
  }, []);

  const handleInsuranceAddressSearchClose = useCallback(() => {
    setIsInsuranceAddressSearchModalOpen(false);
  }, []);

  const handleInsuranceAddressSearchSelect = useCallback(
    (result: {
      postal_code1: string;
      postal_code2: string;
      address: string;
      address_kana: string;
    }) => {
      setValue('health_insurance_card.postal_code1', result.postal_code1, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue('health_insurance_card.postal_code2', result.postal_code2, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue('health_insurance_card.address1', result.address, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue(
        'health_insurance_card.address_kana',
        (result.address_kana || '').slice(0, PATIENT_ADDRESS_KANA_MAX_LENGTH),
        {
          shouldDirty: true,
          shouldTouch: true,
        }
      );
      setIsInsuranceAddressSearchModalOpen(false);
      // toast.success('住所情報を設定しました');
    },
    [setValue]
  );

  const handleInsuranceOpenMap = useCallback(() => {
    const address1 = watch('health_insurance_card.address1');
    // Match A2 Blade behavior: do nothing if address1 is empty
    if (!address1 || address1.trim() === '') {
      return;
    }
    const address = address1 + watch('health_insurance_card.address2');
    const mapsUrl = `https://www.google.co.jp/maps?q=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  }, [watch]);

  const {
    handleSearch: searchInsurers,
    getLastInsurerSearchTotalCount,
    isLoading: isInsurerSearchLoading,
  } = useInsurerSearch({
    onClearInput: () => {
      setValue('health_insurance_card.insurer_number', '');
      // Also clear related fields when insurer search has no results
      setValue('health_insurance_card.insurer_name', '');
      setValue('health_insurance_card.insurer_kind_name', '');
      setValue('health_insurance_card.patient_cost_rate', '');
      setValue('health_insurance_card.patient_cost_id', undefined);
      setValue('health_insurance_card.elderly_insurance_certificate_id', undefined);
      setValue('health_insurance_card.claim_type_id', undefined);
      setValue('health_insurance_card.cost_rate', undefined);
      setValue('health_insurance_card.family_cost_rate', undefined);
    },
  });

  const handleInsurerSearchClose = useCallback(() => {
    setPendingInsurerResults(null);
    setPendingInsurerTotalCount(0);
    setIsInsurerSearchModalOpen(false);
  }, []);

  const clearHealthInsurerFields = useCallback(() => {
    setValue('health_insurance_card.insurer_number', '');
    setValue('health_insurance_card.insurer_name', '');
    setValue('health_insurance_card.insurer_kind_name', '');
    setValue('health_insurance_card.patient_cost_rate', '');
    setValue('health_insurance_card.patient_cost_id', undefined);
    setValue('health_insurance_card.elderly_insurance_certificate_id', undefined);
    setValue('health_insurance_card.claim_type_id', undefined);
    setValue('health_insurance_card.cost_rate', undefined);
    setValue('health_insurance_card.family_cost_rate', undefined);
  }, [setValue]);

  const warnReimbursementIfNeeded = useCallback(
    (claimTypeId: number | null | undefined) => {
      if ((claimTypeId ?? null) === 3) {
        modal.warning({
          title: '償還払い保険者の為、被保険者よりご請求下さい',
          okText: 'OK',
        });
      }
    },
    [modal]
  );

  const warnMedicalAidReimbursementIfNeeded = useCallback(
    (claimTypeId: number | null | undefined) => {
      if ((claimTypeId ?? null) === 3) {
        modal.warning({
          title: '医療助成が償還払い保険者の為、被保険者よりご請求下さい',
          okText: 'OK',
        });
      }
    },
    [modal]
  );

  const handleInsurerSearchSelect = useCallback(
    (result: any) => {
      const fillForm = (detail: any) => {
        if ((detail.medix_claim_type_id ?? null) === 1) {
          const dialogResponse = getWarningDialog1Message();
          handleInsurerSearchClose();
          modal.warning({
            title: '警告',
            content: dialogResponse.message,
            okText: 'OK',
            onOk: () => {
              if (dialogResponse.shouldClearInsurerNumber) {
                clearHealthInsurerFields();
              }
            },
          });
          return;
        }

        if (detail.insurer_number && String(detail.insurer_number).startsWith('39')) {
          setPendingKoreiInsurer(detail);
          setIsKoreiModalOpen(true);
          handleInsurerSearchClose();
          return;
        }

        const insurerKindName =
          detail.insurer_kind_name ||
          (detail.insurer_type && typeof detail.insurer_type.name === 'string'
            ? detail.insurer_type.name
            : null) ||
          '一般';
        setValue('health_insurance_card.insurer_id', detail.id);
        const normalizedInsurerNumber = normalizeInsurerNumberField(
          String(detail.insurer_number ?? '')
        );
        setValue('health_insurance_card.insurer_number', normalizedInsurerNumber);
        setValue('health_insurance_card.insurer_name', detail.insurer_name);
        setValue('health_insurance_card.insurer_kind_name', insurerKindName);
        setValue('health_insurance_card.claim_type_id', detail.medix_claim_type_id ?? null);

        const insurerCostRate = toOptionalNumber(detail.cost_rate);
        const insurerFamilyCostRate = toOptionalNumber(detail.family_cost_rate);
        setValue('health_insurance_card.cost_rate', insurerCostRate);
        setValue('health_insurance_card.family_cost_rate', insurerFamilyCostRate);

        // Auto-set insurance type based on insurer number prefix (same logic as onBlur handler)
        if (normalizedInsurerNumber === '00000001') {
          setValue('health_insurance_card.insurance_type_id', 3); // 生活保護
        } else if (normalizedInsurerNumber.startsWith('39')) {
          setValue('health_insurance_card.insurance_type_id', 2); // 後期高齢
        } else {
          setValue('health_insurance_card.insurance_type_id', 1); // 一般
        }

        const relationshipId = watch('health_insurance_card.relationship_id');
        const detailPatientCostRate =
          detail.patient_cost_rate === undefined ||
          detail.patient_cost_rate === null ||
          String(detail.patient_cost_rate) === ''
            ? undefined
            : String(detail.patient_cost_rate);
        const costRate =
          detailPatientCostRate ??
          (relationshipId === 2
            ? String(insurerFamilyCostRate ?? '')
            : String(insurerCostRate ?? ''));
        setValue('health_insurance_card.patient_cost_rate', costRate);

        // Legacy A2 parity: selecting insurer resets burden override to 初期値.
        setValue('health_insurance_card.patient_cost_id', 0);
        setValue('health_insurance_card.elderly_insurance_certificate_id', 1);

        // toast.success('保険者情報を設定しました');
        handleInsurerSearchClose();
        queueMicrotask(() => onInsurerSelectedNonKoreiRef.current?.());
        warnReimbursementIfNeeded(detail.medix_claim_type_id);
      };

      // List response omits medix_claim_type_id; always fetch detail when we have id + clinic context
      if (result?.id != null && clinic?.site_type && clinic?.clinic_number) {
        fetchInsurerDetail(clinic.site_type, clinic.clinic_number, result.id)
          .then(fillForm)
          .catch(() => {
            toast.error('保険者詳細の取得に失敗しました。');
          });
        return;
      }
      fillForm(result);
    },
    [
      setValue,
      watch,
      handleInsurerSearchClose,
      clearHealthInsurerFields,
      getWarningDialog1Message,
      modal,
      toast,
      clinic,
      warnReimbursementIfNeeded,
      onInsurerSelectedNonKoreiRef,
    ]
  );

  const handleKoreiModalClose = useCallback(() => {
    setValue('health_insurance_card.insurer_number', '');
    setValue('health_insurance_card.claim_type_id', undefined);
    setIsKoreiModalOpen(false);
    setPendingKoreiInsurer(null);
  }, [setValue]);

  const handleKoreiModalSelect = useCallback(
    (rate: number) => {
      if (!pendingKoreiInsurer) return;
      const detail = pendingKoreiInsurer;
      const insurerKindName =
        detail.insurer_kind_name ||
        (detail.insurer_type && typeof detail.insurer_type.name === 'string'
          ? detail.insurer_type.name
          : null) ||
        '一般';

      setValue('health_insurance_card.insurer_id', detail.id);
      setValue(
        'health_insurance_card.insurer_number',
        normalizeInsurerNumberField(String(detail.insurer_number ?? ''))
      );
      setValue('health_insurance_card.insurer_name', detail.insurer_name);
      setValue('health_insurance_card.insurer_kind_name', insurerKindName);
      setValue('health_insurance_card.claim_type_id', detail.medix_claim_type_id ?? null);

      const rateStr = String(rate);
      const rateNumber = toOptionalNumber(rate);
      setValue('health_insurance_card.cost_rate', rateNumber);
      setValue('health_insurance_card.family_cost_rate', rateNumber);
      setValue('health_insurance_card.patient_cost_rate', rateStr);
      setValue('health_insurance_card.patient_cost_id', 0); // "なし"
      setValue('health_insurance_card.elderly_insurance_certificate_id', 1);

      setIsKoreiModalOpen(false);
      setPendingKoreiInsurer(null);
      warnReimbursementIfNeeded(detail.medix_claim_type_id);
    },
    [pendingKoreiInsurer, setValue, warnReimbursementIfNeeded]
  );

  const pendingInsurerSearchRef = useRef<Promise<void> | null>(null);

  const handleInsurerSearch = useCallback(() => {
    const insurerNumber = (watch('health_insurance_card.insurer_number') ?? '').toString().trim();
    const insuranceTypeId = watch('health_insurance_card.insurance_type_id') ?? 1;
    if (!insurerNumber) {
      modal.warning({
        title: '警告',
        content: '保険者番号が入力されていません。',
        okText: 'OK',
      });
      return;
    }
    const searchPromise = searchInsurers(insurerNumber, insuranceTypeId)
      .then(results => {
        if (results?.length === 1) {
          handleInsurerSearchSelect(results[0]);
          return;
        }
        if (results && results.length > 1) {
          setPendingInsurerResults(results);
          setPendingInsurerTotalCount(getLastInsurerSearchTotalCount());
          setIsInsurerSearchModalOpen(true);
          return;
        }
        // 0 or null: useInsurerSearch already showed warning
      })
      .finally(() => {
        pendingInsurerSearchRef.current = null;
      });
    pendingInsurerSearchRef.current = searchPromise;
  }, [watch, searchInsurers, handleInsurerSearchSelect, modal, getLastInsurerSearchTotalCount]);

  /** Wait for any in-flight insurer search triggered by blur. */
  const waitForPendingInsurerSearch = useCallback(async () => {
    if (pendingInsurerSearchRef.current) {
      await pendingInsurerSearchRef.current;
    }
  }, []);

  const { mutateAsync: searchMedicalAidInsurers } = useSearchMedicalAidInsurers(
    clinic?.site_type || '',
    clinic?.clinic_number || ''
  );

  /**
   * Reset everything a 公費負担者 pick fills in (number, rates, partial-aid limits),
   * so a rejected pick does not leave values from the previously applied 公費負担者 behind.
   * 種類, 公費受給者番号 and 適用開始日/適用終了日 are kept: the user entered those
   * independently and only has to re-enter the 公費負担者番号 (UAT Sprint5 No.207).
   */
  const clearMedicalAidInsurerFields = useCallback(() => {
    setValue('medical_aid.medical_aid_number', '');
    setValue('medical_aid.insurer_id', null);
    setValue('medical_aid.insurer_cost_rate', '');
    setValue('medical_aid.cost_rate', '');
    setValue('medical_aid.family_cost_rate', '');
    setValue('medical_aid.is_special', false);
    setValue('medical_aid.is_partial_aid', false);
    setValue('medical_aid.calc_type', null);
    setValue('medical_aid.limit_amount', null);
    setValue('medical_aid.limit_num_month', null);
    setValue('medical_aid.limit_amount_month', null);
    setValue('medical_aid.claim_type_id', null);
  }, [setValue]);

  const handleMedicalAidSearchSelect = useCallback(
    (result: {
      insurer_type_id: number;
      insurer_id: number;
      medical_aid_number: string;
      cost_rate: number | null;
      family_cost_rate: number | null;
      is_special: boolean;
      is_nonoutput_rezept: boolean;
      is_partial_aid: boolean;
      application_start_date?: string;
      application_end_date?: string;
      calc_type?: number | null;
      limit_amount?: number | null;
      limit_num_month?: number | null;
      limit_amount_month?: number | null;
      claim_type_id?: number | null;
      medix_claim_type_id?: number | null;
      is_enabled_invoice?: boolean;
    }): boolean => {
      // HIT-14777: a 公費負担者 whose 請求区分 is 請求不可 is never applied, whichever way it was
      // picked (補助入力 modal, interval table, direct number search). Same flow as the
      // 保険者番号 no-claim warning: close the search, warn, and clear the typed number on OK.
      if (isMedicalAidNoClaim(result)) {
        const dialogResponse = getMedicalAidNoClaimMessage();
        setPendingMedicalAidIntervalRows([]);
        setIsMedicalAidSearchModalOpen(false);
        modal.warning({
          title: '警告',
          content: dialogResponse.message,
          okText: 'OK',
          onOk: () => {
            if (dialogResponse.shouldClearInsurerNumber) {
              clearMedicalAidInsurerFields();
            }
          },
        });
        return false;
      }

      // A2 Step 12 (NO122) treats a type change from the originally loaded value as an error
      // because in A2 the type dropdown was effectively locked — any divergence meant a stale
      // type/number combo. On RA2N the 公費負担者番号 helper (modal pick + direct-number search)
      // is the legitimate way to swap insurers and atomically updates both type and number.
      // Refresh the "original" baseline here so Step 12 only fires for true rogue dropdown edits.
      originalMedicalAidInsurerTypeIdRef.current =
        result.insurer_type_id === 1 ? 0 : result.insurer_type_id;

      // Set medical aid type first (this controls whether other fields are disabled)
      setValue('medical_aid.insurer_type_id', result.insurer_type_id);

      // Set medical aid number (公費負担者番号)
      setValue('medical_aid.medical_aid_number', result.medical_aid_number);

      // Set insurer ID and flags (stored but not displayed in UI)
      setValue('medical_aid.insurer_id', result.insurer_id);
      setValue('medical_aid.is_special', result.is_special);
      setValue('medical_aid.is_partial_aid', result.is_partial_aid);

      // Set cost rate based on relationship (A2: branches on health_insurance_cards.relationship_id)
      // Persist both raw rates so 医療助成負担 can be re-derived if 続柄 changes later (A2 parity).
      const costRate = result.cost_rate ?? 0;
      const familyCostRate = result.family_cost_rate ?? 0;
      setValue('medical_aid.cost_rate', String(costRate));
      setValue('medical_aid.family_cost_rate', String(familyCostRate));
      const relationshipId = watch('health_insurance_card.relationship_id');
      const isSelf = relationshipId === 1 || relationshipId === 3;
      const costRateValue = isSelf ? String(costRate) : String(familyCostRate);
      setValue('medical_aid.insurer_cost_rate', costRateValue);

      // Do not auto-fill application dates from search result

      // A2: specialCalculationCost — always write (null when the insurer has no record) so
      // a prior selection's limits don't leak into the newly-picked insurer.
      setValue('medical_aid.calc_type', result.calc_type ?? null);
      setValue('medical_aid.limit_amount', result.limit_amount ?? null);
      setValue('medical_aid.limit_num_month', result.limit_num_month ?? null);
      setValue('medical_aid.limit_amount_month', result.limit_amount_month ?? null);
      const medicalAidClaimTypeId = result.claim_type_id ?? result.medix_claim_type_id ?? null;
      setValue('medical_aid.claim_type_id', medicalAidClaimTypeId);

      setPendingMedicalAidIntervalRows([]);
      setIsMedicalAidSearchModalOpen(false);
      warnMedicalAidReimbursementIfNeeded(medicalAidClaimTypeId);
      return true;
    },
    [
      setValue,
      watch,
      modal,
      getMedicalAidNoClaimMessage,
      clearMedicalAidInsurerFields,
      warnMedicalAidReimbursementIfNeeded,
      originalMedicalAidInsurerTypeIdRef,
    ]
  );

  const handleMedicalAidNumberDirectSearch = useCallback(
    async (number: string) => {
      const trimmed = number.trim();
      if (!trimmed) return;

      setIsMedicalAidDirectSearchLoading(true);
      setPendingMedicalAidIntervalRows([]);
      try {
        const allResults = await searchMedicalAidInsurers({
          exact_insurer_number: trimmed,
          abort_if_multiple: true,
        });
        const results = allResults || [];

        if (results.length === 1) {
          const [match] = results;
          const applied = handleMedicalAidSearchSelect({
            ...match,
            insurer_id: match.id,
            insurer_type_id: match.insurer_type_id,
            medical_aid_number: match.insurer_number,
            claim_type_id: match.medix_claim_type_id ?? null,
          });
          // Focus 13.4 公費受給者番号
          if (applied) setFocus('medical_aid.medical_aid_patient_number');
          return;
        }

        if (results.length > 1) {
          setPendingMedicalAidIntervalRows(
            [...results].sort((a, b) => (b.sub_number ?? 0) - (a.sub_number ?? 0))
          );
          setIsMedicalAidSearchModalOpen(true);
          return;
        }

        // No results — treat as "not in master"
        modal.error({
          title: getMedicalAidWarningDialog2Message().message,
          okText: 'OK',
        });
      } catch (e) {
        console.error('Direct medical aid search failed', e);
        toast.error('医療助成情報の取得に失敗しました。');
      } finally {
        setIsMedicalAidDirectSearchLoading(false);
      }
    },
    [
      searchMedicalAidInsurers,
      handleMedicalAidSearchSelect,
      setFocus,
      modal,
      toast,
      getMedicalAidWarningDialog2Message,
    ]
  );

  const handleMedicalAidSearch = useCallback(() => {
    const currentNumber = (getValues('medical_aid.medical_aid_number') || '').trim();
    if (currentNumber) {
      void handleMedicalAidNumberDirectSearch(currentNumber);
    } else {
      setPendingMedicalAidIntervalRows([]);
      setIsMedicalAidSearchModalOpen(true);
    }
  }, [getValues, handleMedicalAidNumberDirectSearch]);

  const handleMedicalAidSearchClose = useCallback(() => {
    setPendingMedicalAidIntervalRows([]);
    setIsMedicalAidSearchModalOpen(false);
  }, []);

  return {
    isPatientSearchModalOpen,
    isAddressSearchModalOpen,
    isInsuranceAddressSearchModalOpen,
    isInsurerSearchModalOpen,
    pendingInsurerResults,
    pendingInsurerTotalCount,
    isKoreiModalOpen,
    pendingKoreiInsurer,
    isMedicalAidSearchModalOpen,
    pendingMedicalAidIntervalRows,
    isMedicalAidDirectSearchLoading,
    handlePatientSearch,
    handlePatientSearchClose,
    handlePatientSearchSelect,
    handleAddressSearch,
    handleAddressSearchClose,
    handleAddressSearchSelect,
    handleOpenMap,
    handleInsuranceAddressSearch,
    handleInsuranceAddressSearchClose,
    handleInsuranceAddressSearchSelect,
    handleInsuranceOpenMap,
    handleInsurerSearch,
    isInsurerSearchLoading,
    waitForPendingInsurerSearch,
    handleInsurerSearchClose,
    handleInsurerSearchSelect,
    handleKoreiModalClose,
    handleKoreiModalSelect,
    handleMedicalAidSearch,
    handleMedicalAidSearchClose,
    handleMedicalAidSearchSelect,
    handleMedicalAidNumberDirectSearch,
  };
};
